"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useBillingPlans,
  useSubscribeToBillingPlan,
} from "@/hooks/use-billing";
import {
  getHubspotMeetingUrl,
  HUBSPOT_MEETING_TARGETS,
} from "@/utils/hubspot-meeting";
import { cn } from "@/utils/utils";
import { LoaderCircle, LogOut } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { logout } from "../logout-button";
import { useMutation } from "@tanstack/react-query";
import { BillingPurchaseModes } from "@jurni/schemas";
import { PaywallBookDemoScreen } from "./paywall-book-demo-screen";
import { PaywallPlansScreen } from "./paywall-plans-screen";

interface PaywallPageProps {
  user?: {
    email?: string | null;
    name?: string | null;
  };
  hideBillingPlan?: boolean;
}

export const PaywallPage = ({
  user,
  hideBillingPlan = true,
}: PaywallPageProps) => {
  const router = useRouter();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  const { data, isLoading: plansLoading } = useBillingPlans({
    enabled: !hideBillingPlan,
  });
  const plans = (data?.data || []).filter(
    (plan) => plan.purchaseMode !== BillingPurchaseModes.MANUAL_ONLY,
  );
  const dialogMaxWidthClass = hideBillingPlan
    ? "!max-w-[832px]"
    : plans.length >= 3
      ? "!max-w-[1040px]"
      : "!max-w-[820px]";
  const subscribeToPlan = useSubscribeToBillingPlan();

  const { mutate: handleLogout, isPending: isLogoutPending } = useMutation({
    mutationFn: () => logout(),
  });

  const meetingUrl = useMemo(
    () =>
      getHubspotMeetingUrl({
        email: user?.email ?? undefined,
        name: user?.name ?? undefined,
        target: HUBSPOT_MEETING_TARGETS.PAYWALL_DEMO,
      }),
    [user?.email, user?.name],
  );

  const handleSubscribe = (planId: number) => {
    setSelectedPlanId(planId);
    subscribeToPlan.mutate(
      { planId },
      {
        onSuccess: (result) => {
          // Redirect to Shopify confirmation URL
          if (result.data?.confirmationUrl) {
            router.push(result.data.confirmationUrl);
          } else {
            setSelectedPlanId(null);
          }
        },
        onError: () => {
          setSelectedPlanId(null);
        },
      },
    );
  };

  const handleBookDemo = () => {
    const schedulerWindow = window.open(meetingUrl, "_blank");
    if (!schedulerWindow) {
      return;
    }

    try {
      schedulerWindow.opener = null;
    } catch {
      // Ignore; the scheduler already opened.
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Image
        src="/paywall-background.png"
        alt="Paywall background"
        fill
        priority
        className="scale-105 object-cover blur-[5px]"
      />
      <div className="absolute inset-0 bg-white/45" />
      <div className="relative z-10 flex min-h-screen w-full items-center justify-center px-4 py-6">
        <Dialog open>
          <DialogContent
            className={cn(
              "flex max-h-[92dvh] w-[calc(100vw-32px)] min-w-0 flex-col gap-0 overflow-hidden p-0",
              hideBillingPlan
                ? "h-[535px] !max-w-[832px] !rounded-[20px] border-0 bg-[#f5f5f6] shadow-[0px_0px_0px_1px_rgba(37,37,39,0.08),0px_8px_16px_0px_rgba(37,37,39,0.08),0px_16px_32px_0px_rgba(37,37,39,0.08)]"
                : "!rounded-[22px] border border-zinc-200 bg-[#fbfbfb] shadow-[0_24px_90px_rgba(0,0,0,0.22)]",
              dialogMaxWidthClass,
            )}
            closable={false}
            overlayClassName="bg-black/10"
            animate={false}
            withPortal={false}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {hideBillingPlan ? (
              <>
                <DialogTitle className="sr-only">Book a Demo</DialogTitle>
                <PaywallBookDemoScreen
                  onBookDemo={handleBookDemo}
                  onLogout={() => handleLogout()}
                  isLogoutPending={isLogoutPending}
                />
              </>
            ) : (
              <>
                <DialogHeader className="flex-shrink-0 flex-row items-center justify-between gap-4 space-y-0 px-5 pt-5 sm:px-6">
                  <DialogTitle className="truncate text-xl font-semibold leading-7">
                    Choose a Plan
                  </DialogTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
                    onClick={() => handleLogout()}
                    disabled={isLogoutPending}
                    aria-label="Log out"
                  >
                    {isLogoutPending ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <LogOut className="h-4 w-4" />
                    )}
                  </Button>
                </DialogHeader>
                <PaywallPlansScreen
                  plans={plans}
                  isLoading={plansLoading}
                  selectedPlanId={selectedPlanId}
                  onSubscribe={handleSubscribe}
                  onBookDemo={handleBookDemo}
                />
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
